<template>
	<BootstrapVueNextModal
		v-bind="$attrs"
		:id="id"
		ref="modal"
		:title="title"
		:modal-class="normalizedModalClass"
		:no-header-close="noHeaderClose" :model-value="modalVisible"
		@hide="$emit('hide', $event)"
		@hidden="onHidden"
		@hide-prevented="$emit('hide-prevented', $event)"
		@show="onShow"
		@shown="$emit('shown', $event)"
		@show-prevented="$emit('show-prevented', $event)"
		@toggle="$emit('toggle', $event)"
		@toggle-prevented="$emit('toggle-prevented', $event)"
		@ok="$emit('ok', $event)"
		@cancel="$emit('cancel', $event)"
		@close="$emit('close', $event)"
		@esc="$emit('esc', $event)"
		@backdrop="$emit('backdrop', $event)"
		@update:model-value="modalVisible = $event">
		<template v-if="!$slots.header" #header="{ close }">
			<h5 class="modal-title">
				{{ title }}
			</h5>
			<button
				v-if="!noHeaderClose"
				type="button"
				class="close"
				aria-label="Close"
				@click="close">
				<span aria-hidden="true">&times;</span>
			</button>
		</template>
		<template v-if="!$slots.backdrop" #backdrop="{ active, hide }">
			<div
				v-show="active"
				:class="['modal-backdrop', 'fade', 'show', backdropStackClass]"
				@click="hide('backdrop')"></div>
		</template>
		<template v-for="(_, name) in $slots" #[name]="slotProps">
			<slot :name="name" v-bind="slotProps"></slot>
		</template>
	</BootstrapVueNextModal>
</template>

<script>
	import { BModal as BootstrapVueNextModal } from 'bootstrap-vue-next';
	import { registerModal } from '@js/utilities/bootstrap-vue-modal.js';
	import { useModalStack } from '@js/composables/useModalStack.js';

	export default {
		name: 'BModal',
		// Written for Vue 3 (modelValue v-model, class and style in $attrs), so skip the Vue 2 compat rewrites
		compatConfig: { MODE: 3 },
		components: {
			BootstrapVueNextModal,
		},
		inheritAttrs: false,
		props: {
			id: {
				type: String,
				default: null,
			},
			modelValue: {
				type: Boolean,
				default: false,
			},
			value: {
				type: Boolean,
				default: false,
			},
			visible: {
				type: Boolean,
				default: false,
			},
			title: {
				type: String,
				default: '',
			},
			noHeaderClose: {
				type: Boolean,
				default: false,
			},
			modalClass: {
				type: [String, Array, Object],
				default: null,
			},
		},
		emits: [
			'backdrop',
			'cancel',
			'close',
			'esc',
			'hide',
			'hidden',
			'hide-prevented',
			'ok',
			'show',
			'shown',
			'show-prevented',
			'toggle',
			'toggle-prevented',
			'change',
			'update:modelValue',
		],
		setup() {
			return {
				...useModalStack(),
			};
		},
		data() {
			return {
				localVisible: this.modelValue !== undefined ? this.modelValue : this.value || this.visible,
				unregisterModal: null,
			}
		},
		computed: {
			modalVisible: {
				get() {
					return this.localVisible;
				},
				set(value) {
					this.localVisible = value;
					this.$emit('update:modelValue', value);
					this.$emit('change', value);
				},
			},
			normalizedModalClass() {
				return [
					this.modalClass,
					this.modalStackClass,
				].filter(Boolean);
			},
		},
		watch: {
			modelValue(value) {
				this.localVisible = value;
				this.syncModal(value);
			},
			value(value) {
				this.localVisible = value;
				this.syncModal(value);
			},
			visible(value) {
				this.localVisible = value;
				this.syncModal(value);
			},
			id: {
				immediate: true,
				handler() {
					this.register();
				},
			},
		},
		beforeUnmount() {
			this.unregisterModal?.();
		},
		methods: {
			register() {
				this.unregisterModal?.();
				this.unregisterModal = registerModal(this.id, {
					show: this.show,
					hide: this.hide,
				});
			},
			show() {
				this.modalVisible = true;
				this.$refs.modal?.show?.();
			},
			hide() {
				this.modalVisible = false;
				this.$refs.modal?.hide?.();
			},
			syncModal(value) {
				this.$nextTick(() => {
					if (value) {
						this.$refs.modal?.show?.();
						return;
					}

					this.$refs.modal?.hide?.();
				});
			},
			onHidden(event) {
				this.modalVisible = false;
				this.releaseModalStack();
				this.$emit('hidden', event);
			},
			onShow(event) {
				this.activateModalStack();
				this.$emit('show', event);
			},
		},
	}
</script>
